import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { api, useAuth } from "@/context/AuthContext";
import TrainingProgressDashboard from "@/components/training/TrainingProgressDashboard";
import PracticeHoursCard from "@/components/learner/PracticeHoursCard";
import LearnerTrainingOutcomeCard from "@/components/learner/LearnerTrainingOutcomeCard";
import { trainingInterestForProgramme } from "@/lib/pathwayInterest";
import SketchModal from "@/components/sketch/SketchModal";

const CTA_MAILTO =
  "mailto:info@getdrivinginstructor.co.nz?subject=Contact%20us%20about%20learner%20training";

export default function Dashboard() {
  const { user, authReady } = useAuth();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [catalog, setCatalog] = useState([]);
  const [exploreCatalog, setExploreCatalog] = useState([]);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState([]);
  const [progression, setProgression] = useState(null);
  const [feedbackMeta, setFeedbackMeta] = useState(null);
  const [sketches, setSketches] = useState([]);
  const [viewingSketch, setViewingSketch] = useState(null);
  const [sketchModalOpen, setSketchModalOpen] = useState(false);
  const [sketchLoadError, setSketchLoadError] = useState("");
  const [trainingRequests, setTrainingRequests] = useState([]);
  const [trainingInterest, setTrainingInterest] = useState("general_learner");
  const [trainingSubmitting, setTrainingSubmitting] = useState(false);
  const [pathwayRequestBusy, setPathwayRequestBusy] = useState(false);
  const [pathwayRequestNotice, setPathwayRequestNotice] = useState("");

  const programmeId = searchParams.get("programme_id") || undefined;
  const isLearner = user?.role === "learner";

  const loadCatalogue = useCallback(() => {
    return api
      .get("/programmes")
      .then((res) => {
        setCatalog(res.data.programmes || []);
        setExploreCatalog(res.data.explore_programmes || []);
      })
      .catch(() => {});
  }, []);

  const loadTrainingRequest = useCallback(() => {
    if (!isLearner) return Promise.resolve();
    return api
      .get("/access-workflow/training-requests/me")
      .then((res) => setTrainingRequests(res.data.requests || []))
      .catch(() => setTrainingRequests([]));
  }, [isLearner]);

  const loadProgramme = useCallback(() => {
    setError("");
    setLoading(true);
    const params = { _ts: Date.now() };
    if (programmeId) params.programme_id = programmeId;

    const attempt = (isRetry) =>
      api
        .get("/programme", { params: { ...params, _ts: Date.now() } })
        .then((res) => {
          setData(res.data);
          if (res.data.programme_id && !programmeId) {
            setSearchParams({ programme_id: res.data.programme_id }, { replace: true });
          }
        })
        .catch((err) => {
          const status = err?.response?.status;
          // Free-tier cold start / transient network: one delayed retry before opaque failure.
          if (!isRetry && (!status || status >= 500 || status === 408 || status === 503)) {
            return new Promise((resolve) => setTimeout(resolve, 2500)).then(() => attempt(true));
          }
          if (status === 401) {
            setError("Your session expired. Please log in again.");
          } else if (status === 403) {
            setError("You are not enrolled in this programme yet.");
          } else {
            setError("Could not load your programme. Please try again.");
          }
        });

    return attempt(false).finally(() => setLoading(false));
  }, [programmeId, setSearchParams]);

  const loadFeedback = useCallback(() => {
    const pid = programmeId;
    if (!pid) return Promise.resolve();
    return Promise.all([
      api
        .get("/learner/training-feedback", { params: { programme_id: pid } })
        .then((res) => {
          setFeedback(res.data.records || []);
          setFeedbackMeta({
            training_entitlement: res.data.training_entitlement,
            message: res.data.message,
          });
        })
        .catch(() => {
          setFeedback([]);
          setFeedbackMeta(null);
        }),
      api
        .get("/learner/training-progression", { params: { programme_id: pid } })
        .then((res) => setProgression(res.data))
        .catch(() => setProgression(null)),
      api
        .get("/learner/training-sketches")
        .then((res) => setSketches(res.data.sketches || []))
        .catch(() => setSketches([])),
    ]);
  }, [programmeId]);

  useEffect(() => {
    if (!authReady) return undefined;
    if (location.pathname !== "/dashboard") return undefined;
    loadCatalogue();
    loadTrainingRequest();
    loadProgramme().then(loadFeedback);
    const onPageShow = (e) => {
      if (e.persisted) loadProgramme().then(loadFeedback);
    };
    const onFocus = () => loadProgramme().then(loadFeedback);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("focus", onFocus);
    };
  }, [authReady, location.pathname, location.key, loadProgramme, loadCatalogue, loadFeedback, loadTrainingRequest]);

  const enrol = async (pid) => {
    try {
      await api.post("/enrolments", { programme_id: pid });
      setSearchParams({ programme_id: pid });
      await loadCatalogue();
      await loadProgramme();
    } catch {
      setError("Could not enrol in that programme.");
    }
  };

  const requestPathwayAccess = async (programme) => {
    const pid = programme.id;
    if (programme.can_self_enrol) {
      await enrol(pid);
      return;
    }
    const interest = trainingInterestForProgramme(pid);
    if (!interest) {
      setError("This pathway cannot be requested from the dashboard.");
      return;
    }
    setPathwayRequestBusy(true);
    setPathwayRequestNotice("");
    setError("");
    try {
      const res = await api.post("/access-workflow/training-requests", { interest });
      const msg = res.data?.message || "Training request recorded.";
      setPathwayRequestNotice(msg);
      await loadTrainingRequest();
    } catch {
      setError("Could not record your training request. Please try again.");
    } finally {
      setPathwayRequestBusy(false);
    }
  };

  if (!authReady || (loading && !data)) {
    return <p data-testid="dashboard-loading">Loading your dashboard…</p>;
  }
  if (error && !data) {
    return <p role="alert" className="text-destructive" data-testid="dashboard-error">{error}</p>;
  }
  if (!data) {
    return <p data-testid="dashboard-loading">Loading your dashboard…</p>;
  }

  const { programme, modules, lessons, progress_percent } = data;
  const moduleList = modules?.length ? modules : data.module ? [data.module] : [];
  const flatLessons = Array.isArray(lessons) && lessons.length
    ? lessons
    : moduleList.flatMap((m) => (m.lessons || []).map((l) => ({ ...l, module_title: m.title })));

  const nextLesson = data.next_lesson_id
    ? flatLessons.find((l) => l.id === data.next_lesson_id) || data.next_lesson
    : null;
  const programmeComplete =
    data.programme_complete === true ||
    (progress_percent === 100 && flatLessons.length > 0 && flatLessons.every((l) => l.completed));

  const statusCounts = flatLessons.reduce(
    (acc, l) => {
      const s =
        l.learner_status || (l.completed ? "completed" : l.needs_retry ? "needs_retry" : "not_started");
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    },
    { completed: 0, needs_retry: 0, not_started: 0 }
  );

  const lessonHref = (id) =>
    `/lessons/${id}${programme?.id ? `?programme_id=${encodeURIComponent(programme.id)}` : ""}`;

  return (
    <div className="space-y-8" data-testid="learner-dashboard">
      <header className="space-y-1">
        <h1 className="text-4xl sm:text-5xl font-bold text-primary" data-testid="dashboard-greeting">
          Kia ora, {user.display_name}
        </h1>
        <p className="text-base md:text-lg text-muted-foreground">Your learning dashboard</p>
      </header>

      {catalog.length > 0 && (
        <section className="card space-y-3" data-testid="programme-catalogue">
          <h2 className="text-base md:text-lg font-semibold">Your programmes</h2>
          <ul className="space-y-2">
            {catalog.map((p) => (
              <li key={p.id} className="flex flex-wrap items-center gap-2 justify-between">
                <button
                  type="button"
                  className={`text-left underline ${programme?.id === p.id ? "font-semibold" : ""}`}
                  data-testid={`programme-select-${p.slug || p.id}`}
                  onClick={() => (p.enrolled ? setSearchParams({ programme_id: p.id }) : enrol(p.id))}
                >
                  {p.name || p.title}
                </button>
                {!p.enrolled && user.role === "learner" && (
                  <button type="button" className="btn-outline" onClick={() => enrol(p.id)} data-testid={`enrol-${p.id}`}>
                    Enrol
                  </button>
                )}
              </li>
            ))}
          </ul>
          <p className="text-sm text-muted-foreground">
            Unpublished programmes never appear here. Progress is isolated per programme.
          </p>
        </section>
      )}

      {exploreCatalog.length > 0 && (
        <section className="card space-y-3" data-testid="programme-explore">
          <h2 className="text-base md:text-lg font-semibold">Explore pathways</h2>
          <p className="text-sm text-muted-foreground">
            Advanced pathways need eligible booking verification and instructor activation. Use Request access to record
            your interest; enrolment unlocks after instructor activation.
          </p>
          {pathwayRequestNotice && (
            <p className="text-sm text-muted-foreground" role="status" data-testid="explore-pathway-request-notice">
              {pathwayRequestNotice}
            </p>
          )}
          <ul className="space-y-2">
            {exploreCatalog.map((p) => (
              <li key={p.id} className="flex flex-wrap gap-2 justify-between items-center">
                <span>{p.name || p.title}</span>
                <button
                  type="button"
                  className="btn-outline"
                  disabled={pathwayRequestBusy}
                  title={p.can_self_enrol ? "" : p.enrol_blocked_reason || "Records a training request for admin review"}
                  data-testid={`explore-request-${p.id}`}
                  onClick={() => requestPathwayAccess(p)}
                >
                  {p.can_self_enrol ? "Enrol" : "Request access"}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="card space-y-4" data-testid="programme-card">
        <div className="space-y-1">
          <h2 className="text-base md:text-lg font-semibold" data-testid="programme-title">
            {programme.title}
          </h2>
          <p className="text-muted-foreground">{programme.description}</p>
          {programme.qualification_claim === false && (
            <p className="text-sm text-muted-foreground" data-testid="qualification-disclaimer">
              Educational programme only — not a regulatory licence qualification.
            </p>
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">Progress</span>
            <span data-testid="progress-percent">{progress_percent}%</span>
          </div>
          <div
            className="progress-track"
            role="progressbar"
            aria-valuenow={progress_percent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Programme progress"
            data-testid="progress-bar"
          >
            <div className="progress-fill" style={{ width: `${progress_percent}%` }} />
          </div>
          <p
            className={`text-sm inline-block px-2 py-1 rounded ${programmeComplete ? "status-success" : ""}`}
            data-testid="completion-state"
          >
            Status: {programmeComplete ? "Completed" : "In progress"}
          </p>
        </div>

        <div className="flex flex-wrap gap-2" data-testid="status-summary" aria-label="Lesson status summary">
          <span className="stat-chip stat-completed" data-testid="status-count-completed">
            Completed: {statusCounts.completed}
          </span>
          <span className="stat-chip stat-retry" data-testid="status-count-needs-retry">
            Needs retry: {statusCounts.needs_retry}
          </span>
          <span className="stat-chip stat-notstarted" data-testid="status-count-not-started">
            Not started: {statusCounts.not_started}
          </span>
        </div>

        {nextLesson && !programmeComplete && (
          <Link className="btn-accent inline-flex" to={lessonHref(nextLesson.id)} data-testid="continue-learning">
            Continue learning
          </Link>
        )}
        {data.review_queue_available && (
          <button
            type="button"
            className="btn-outline"
            data-testid="start-review-queue"
            onClick={async () => {
              await api.post("/programme/review-queue", {
                enabled: true,
                programme_id: programme.id,
              });
              await loadProgramme();
            }}
          >
            Start Needs retry review
          </button>
        )}
      </section>

      {isLearner && (
        <section className="card space-y-3" data-testid="training-interest-card">
          <h2 className="text-base md:text-lg font-semibold">Instructor-supported training</h2>
          <p className="text-sm text-muted-foreground">
            Try our free online quiz. Additional Academy access is available with eligible driving-lesson bookings.
            Access conditions apply. Instructor activation is required for advanced access. All over Auckland.
          </p>
          {trainingRequests.length > 0 && (
            <ul className="text-sm space-y-1" data-testid="training-request-list">
              {trainingRequests.map((r) => (
                <li key={r.id} data-testid={`training-request-${r.id}`}>
                  {r.interest.replace(/_/g, " ")} — {r.status}
                  {r.status === "pending" && (
                    <span className="text-muted-foreground">
                      {" "}
                      (waiting for administrator to assign an instructor; practical training stays off until activation)
                    </span>
                  )}
                  {r.status === "assigned" && r.assigned_instructor_display_name && (
                    <span data-testid="assigned-instructor-name">
                      {" "}
                      — Instructor: {r.assigned_instructor_display_name}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
          <form
              className="flex flex-wrap gap-2 items-end"
              onSubmit={async (e) => {
                e.preventDefault();
                setTrainingSubmitting(true);
                try {
                  await api.post("/access-workflow/training-requests", { interest: trainingInterest });
                  await loadTrainingRequest();
                } catch {
                  setError("Could not submit training request.");
                } finally {
                  setTrainingSubmitting(false);
                }
              }}
            >
              <label className="sr-only" htmlFor="dash-training-interest">Training interest</label>
              <select
                id="dash-training-interest"
                className="input max-w-md"
                value={trainingInterest}
                onChange={(e) => setTrainingInterest(e.target.value)}
              >
                <option value="general_learner">General learner driving</option>
                <option value="manual_driving">Manual driving</option>
                <option value="overseas_driver">Overseas-driver support</option>
                <option value="older_driver_refresher">Older-driver / refresher</option>
                <option value="school_university">School / university</option>
              </select>
              <button type="submit" className="btn-outline" disabled={trainingSubmitting}>
                Submit interest (not enrolment)
              </button>
            </form>
        </section>
      )}

      {isLearner && <PracticeHoursCard programmeId={programmeId || "programme-nz-road-code"} />}
      {isLearner && <LearnerTrainingOutcomeCard programmeId={programmeId || "programme-nz-road-code"} />}

      <section className="card space-y-3" data-testid="theory-entry-card">
        <h2 className="text-base md:text-lg font-semibold">Theory Test Preparation</h2>
        <p className="text-muted-foreground">
          Practise approved learner licence theory questions, or sit a 35-question practice mock test.
        </p>
        <Link to="/theory" className="btn-primary inline-flex" data-testid="theory-entry-link">
          Open Theory Test Preparation
        </Link>
      </section>

      <section className="card space-y-3" data-testid="learning-preferences-entry-card">
        <h2 className="text-base md:text-lg font-semibold">Learning preferences</h2>
        <p className="text-muted-foreground">
          Tell your instructor what helps you learn best — optional, and never affects your scores.
        </p>
        <Link to="/learning-preferences" className="btn-outline inline-flex" data-testid="learning-preferences-entry-link">
          Set my learning preferences
        </Link>
      </section>

      {moduleList.map((mod) => (
        <section key={mod.id} className="space-y-3" data-testid={`module-${mod.id}`}>
          <h3 className="text-lg font-semibold">{mod.title}</h3>
          <ul className="space-y-2">
            {(mod.lessons || flatLessons.filter((l) => l.module_id === mod.id)).map((lesson) => {
              const status =
                lesson.learner_status ||
                (lesson.completed ? "completed" : lesson.needs_retry ? "needs_retry" : "not_started");
              return (
                <li key={lesson.id} className="card flex flex-wrap items-center justify-between gap-2">
                  <Link to={lessonHref(lesson.id)} className="font-medium underline" data-testid={`lesson-link-${lesson.id}`}>
                    {lesson.title}
                  </Link>
                  <span className={`stat-chip stat-${status === "needs_retry" ? "retry" : status === "completed" ? "completed" : "notstarted"}`}>
                    {status === "needs_retry" ? "Needs retry" : status === "completed" ? "Completed" : "Not started"}
                  </span>
                </li>
              );
            })}
          </ul>
        </section>
      ))}

      <section className="card space-y-3" data-testid="learner-training-feedback">
        <h2 className="text-base md:text-lg font-semibold">Published training feedback</h2>
        <p className="text-sm text-muted-foreground">
          Only published records appear here. Draft and approved-but-unpublished notes stay private to your instructor.
        </p>
        {feedbackMeta?.training_entitlement === "PUBLIC_BASIC" && !feedback.length && (
          <p className="text-sm" data-testid="training-entitlement-basic">
            {feedbackMeta.message ||
              "No published practical feedback yet. Theory practice remains available; new practical training requires instructor activation."}
          </p>
        )}
        {feedbackMeta?.training_entitlement === "PUBLIC_BASIC" && feedback.length > 0 && (
          <p className="text-sm text-muted-foreground" data-testid="training-history-retained">
            Your published training history stays available here. New practical sessions require instructor activation.
          </p>
        )}
        <ul className="space-y-3">
          {feedback.map((r) => (
            <li key={r.id} className="border-t border-border pt-3 space-y-1" data-testid={`published-feedback-${r.id}`}>
              <p className="font-medium">
                {r.session_date} {r.session_time_ampm} — {r.workflow_kind || `Mode ${r.training_mode || "A"}`} — {r.instructor_name}
              </p>
              {(r.quick_lesson_task_labels || []).length > 0 && (
                <p data-testid={`tasks-${r.id}`}>Activities practised: {(r.quick_lesson_task_labels || []).join(", ")}</p>
              )}
              {r.strengths && <p>Went well: {r.strengths}</p>}
              {r.areas_for_improvement && <p>Needs work: {r.areas_for_improvement}</p>}
              {r.learner_visible_notes && <p>{r.learner_visible_notes}</p>}
              {r.next_session_focus && <p>Next lesson: {r.next_session_focus}</p>}
              {r.readiness_state && (
                <p data-testid={`readiness-${r.id}`}>
                  Readiness: {r.readiness_state} (educational estimate — not an NZTA result)
                </p>
              )}
              {r.score_visibility === "VISIBLE" &&
                Array.isArray(r.competency_assessments) &&
                r.competency_assessments.some((a) => a && a.score != null) && (
                  <ul className="text-sm space-y-1" data-testid={`scores-${r.id}`}>
                    {r.competency_assessments
                      .filter((a) => a && a.score != null)
                      .map((a) => (
                        <li key={`${r.id}-${a.competency_id}`}>
                          {a.name || a.competency_id}: {a.score}/5
                          {a.rubric ? ` — ${a.rubric}` : ""}
                        </li>
                      ))}
                  </ul>
                )}
              <p className="text-xs text-muted-foreground">External share: {r.external_share_state || "NOT_SHAREABLE_EXTERNALLY"}</p>
            </li>
          ))}
          {!feedback.length && feedbackMeta?.training_entitlement !== "PUBLIC_BASIC" && (
            <li className="text-muted-foreground">No published training feedback yet.</li>
          )}
        </ul>
      </section>

      {progression &&
        (progression.training_sessions_recorded > 0 ||
          progression.latest_competency_status?.length > 0 ||
          progression.mode_a_history?.length > 0 ||
          progression.readiness_history?.length > 0 ||
          progression.latest_next_session_focus) && (
          <section className="card space-y-4" data-testid="learner-training-progression">
            <h2 className="text-base md:text-lg font-semibold">Training progress</h2>
            <p className="text-sm text-muted-foreground">
              Based on Academy educational Training feedback records only — not Operational GDI paid lesson counts.
            </p>

            <div data-testid="progress-overview">
              <h3 className="font-semibold text-sm">Current overview</h3>
              <p data-testid="training-sessions-recorded">
                Training feedback records: {progression.training_sessions_recorded || 0}
              </p>
            </div>

            <TrainingProgressDashboard progression={progression} testIdPrefix="learner" />

            {progression.latest_next_session_focus && (
              <div data-testid="progress-next-focus-block">
                <h3 className="font-semibold text-sm">Next focus</h3>
                <p data-testid="progression-next-focus">{progression.latest_next_session_focus}</p>
              </div>
            )}

            {progression.strongest?.length > 0 && (
              <div>
                <h3 className="font-semibold text-sm">Strengths</h3>
                <p data-testid="progression-strongest">
                  {progression.strongest.map((s) => s.term).join(", ")}
                </p>
              </div>
            )}

            {progression.development_areas?.length > 0 && (
              <div>
                <h3 className="font-semibold text-sm">Development areas</h3>
                <p data-testid="progression-develop">
                  {progression.development_areas.map((s) => s.term).join(", ")}
                </p>
              </div>
            )}

            {(progression.chronological_history || []).length > 0 && (
              <div>
                <h3 className="font-semibold text-sm">Recent training</h3>
                <ul className="text-sm space-y-2" data-testid="progression-chronology">
                  {[...(progression.chronological_history || [])].reverse().slice(0, 8).map((h) => (
                    <li key={h.id}>
                      {h.session_date} {h.session_time_ampm} — {h.workflow_kind}
                      {(h.task_labels || []).length ? ` · ${(h.task_labels || []).join(", ")}` : ""}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {progression.skills_practised?.length > 0 && (
              <div>
                <h3 className="font-semibold text-sm">Skills practised</h3>
                <p className="text-sm" data-testid="progression-skills">
                  {progression.skills_practised.map((s) => `${s.label} (${s.count})`).join(", ")}
                </p>
              </div>
            )}

            {progression.score_trends && Object.keys(progression.score_trends).length > 0 && (
              <div>
                <h3 className="font-semibold text-sm">Competency trends</h3>
                <ul className="text-sm space-y-1" data-testid="progression-score-trends">
                  {Object.values(progression.score_trends).map((t) => (
                    <li key={t.competency_id}>
                      {t.name}: {(t.scores || []).join(" → ")}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {progression.daily_task_trends && Object.keys(progression.daily_task_trends).length > 0 && (
              <div>
                <h3 className="font-semibold text-sm">Task trends (recent daily feedback)</h3>
                <ul className="text-sm space-y-1" data-testid="progression-daily-task-trends">
                  {Object.values(progression.daily_task_trends).map((t) => (
                    <li key={t.task_id}>
                      {t.name}: {(t.scores || []).join(" → ")}
                      <span className="text-muted-foreground"> — training progress only</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {progression.mock_readiness_latest && (
              <div data-testid="progression-mock-readiness">
                <h3 className="font-semibold text-sm">Mock / readiness</h3>
                <p>
                  Latest: {progression.mock_readiness_latest.readiness_state} ({progression.mock_readiness_latest.session_date})
                  <span className="text-muted-foreground"> — educational estimate, not an NZTA result</span>
                </p>
              </div>
            )}

            {progression.ai_longitudinal_summary && (
              <p className="text-xs text-muted-foreground" data-testid="ai-longitudinal-hold">
                AI progress summary: architecture ready · runtime HOLD
              </p>
            )}
          </section>
        )}

      {sketches.length > 0 && (
        <section className="card space-y-3" data-testid="learner-visible-sketches">
          <h2 className="text-base md:text-lg font-semibold">Lesson sketches</h2>
          <p className="text-sm text-muted-foreground">
            Diagrams your instructor shared with you for this training.
          </p>
          <ul className="space-y-2">
            {sketches.map((s) => (
              <li key={s.sketch_id} className="text-sm border-t border-border pt-2" data-testid={`learner-sketch-${s.sketch_id}`}>
                <p className="font-medium">{s.title || "Sketch"}</p>
                {s.description ? <p>{s.description}</p> : null}
                <p className="text-xs text-muted-foreground">{s.template_id} · updated {s.updated_at}</p>
                <button
                  type="button"
                  className="btn-outline min-h-11 mt-2"
                  data-testid={`learner-open-sketch-${s.sketch_id}`}
                  onClick={async () => {
                    setSketchLoadError("");
                    try {
                      const res = await api.get(`/learner/training-sketches/${s.sketch_id}`);
                      setViewingSketch(res.data.sketch);
                      setSketchModalOpen(true);
                    } catch {
                      setSketchLoadError("This sketch could not be opened. Ask your instructor to share it with you.");
                    }
                  }}
                >
                  View sketch
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {sketchLoadError && (
        <p className="text-sm text-destructive" role="alert" data-testid="learner-sketch-load-error">
          {sketchLoadError}
        </p>
      )}

      <SketchModal
        open={sketchModalOpen}
        onClose={() => {
          setSketchModalOpen(false);
          setViewingSketch(null);
        }}
        readOnly
        heading={viewingSketch?.title || "Lesson sketch"}
        initialTitle={viewingSketch?.title || ""}
        initialDescription={viewingSketch?.description || ""}
        initialTemplateId={viewingSketch?.template_id || "blank"}
        initialStrokesJson={viewingSketch?.strokes_json || ""}
        initialPages={viewingSketch?.pages || null}
      />

      <p className="text-sm">
        <a className="underline" href={CTA_MAILTO} data-testid="dashboard-contact">
          Contact us about learner training
        </a>
      </p>
    </div>
  );
}
